import React, { useEffect, useState } from "react";

import { lessonOptionsService } from "api/services/lesson-options.service";
import { lessonProductsService } from "api/services/lesson-products.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";
import { extractList, getApiErrorMessage } from "utils/api-error";

import type { LessonOptionGroup } from "utils/type/product-option";
import "pages/AdminPage.scss";
import "./ProductVariants.scss";

interface LessonProductItem {
  id: number;
  name: string;
  sport_name: string;
  has_options: boolean;
}

const ProductVariants: React.FC = () => {
  const [products, setProducts] = useState<LessonProductItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<LessonProductItem | null>(null);
  const [optionGroups, setOptionGroups] = useState<LessonOptionGroup[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  // 그룹 생성/수정 모달
  const [groupModal, setGroupModal] = useState<{
    open: boolean;
    editing: LessonOptionGroup | null;
  }>({ open: false, editing: null });
  const [groupForm, setGroupForm] = useState({
    name: "",
    is_required: false,
    allow_multiple: false,
    order: 0,
  });

  // 값 생성 모달
  const [valueModal, setValueModal] = useState<{
    open: boolean;
    groupId: number | null;
  }>({ open: false, groupId: null });
  const [valueForm, setValueForm] = useState({
    name: "",
    price_delta: "0",
    order: 0,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchProducts = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await lessonProductsService.list();
      setProducts(extractList<LessonProductItem>(res.data));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchOptions = async (productId: number) => {
    setOptionsLoading(true);
    try {
      const res = await lessonOptionsService.listGroups(productId);
      setOptionGroups(extractList<LessonOptionGroup>(res.data));
    } catch (err) {
      setOptionGroups([]);
      alert(getApiErrorMessage(err));
    } finally {
      setOptionsLoading(false);
    }
  };

  const selectProduct = (product: LessonProductItem) => {
    setSelectedProduct(product);
    fetchOptions(product.id);
  };

  const openGroupModal = (group?: LessonOptionGroup) => {
    if (group) {
      setGroupForm({
        name: group.name,
        is_required: group.is_required,
        allow_multiple: group.allow_multiple,
        order: group.order,
      });
      setGroupModal({ open: true, editing: group });
    } else {
      setGroupForm({ name: "", is_required: false, allow_multiple: false, order: 0 });
      setGroupModal({ open: true, editing: null });
    }
  };

  const handleSaveGroup = async () => {
    if (!selectedProduct) return;
    setSaving(true);
    try {
      if (groupModal.editing) {
        await lessonOptionsService.updateGroup(selectedProduct.id, groupModal.editing.id, groupForm);
      } else {
        await lessonOptionsService.createGroup(selectedProduct.id, groupForm);
      }
      setGroupModal({ open: false, editing: null });
      fetchOptions(selectedProduct.id);
    } catch (err) {
      alert(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    if (!selectedProduct || !window.confirm("이 옵션 그룹을 삭제하시겠습니까?")) return;
    try {
      await lessonOptionsService.deleteGroup(selectedProduct.id, groupId);
      fetchOptions(selectedProduct.id);
    } catch (err) {
      alert(getApiErrorMessage(err));
    }
  };

  const openValueModal = (groupId: number) => {
    setValueForm({ name: "", price_delta: "0", order: 0 });
    setValueModal({ open: true, groupId });
  };

  const handleSaveValue = async () => {
    if (!selectedProduct || !valueModal.groupId) return;
    setSaving(true);
    try {
      await lessonOptionsService.createValue(selectedProduct.id, valueModal.groupId, {
        ...valueForm,
        price_delta: Number(valueForm.price_delta),
      });
      setValueModal({ open: false, groupId: null });
      fetchOptions(selectedProduct.id);
    } catch (err) {
      alert(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteValue = async (groupId: number, valueId: number) => {
    if (!selectedProduct || !window.confirm("이 옵션 값을 삭제하시겠습니까?")) return;
    try {
      await lessonOptionsService.deleteValue(selectedProduct.id, groupId, valueId);
      fetchOptions(selectedProduct.id);
    } catch (err) {
      alert(getApiErrorMessage(err));
    }
  };

  const productColumns: Column<LessonProductItem>[] = [
    { key: "name", title: "상품명", width: "250px" },
    { key: "sport_name", title: "종목", width: "100px" },
    {
      key: "has_options",
      title: "옵션",
      width: "70px",
      render: (v) => (v ? <Badge variant="active">있음</Badge> : <Badge variant="draft">없음</Badge>),
    },
    {
      key: "actions",
      title: "",
      width: "80px",
      render: (_, row) => (
        <Button size="sm" variant="ghost" onClick={() => selectProduct(row)}>
          옵션 관리
        </Button>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader title="상품 옵션/재고 관리" description="강습 상품의 옵션 그룹과 값을 관리합니다." />

      <div className="admin-page__card">
        {error && <div className="admin-page__error">{error}</div>}
        {loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={productColumns} data={products} rowKey="id" />}
      </div>

      {/* 옵션 관리 영역 */}
      {selectedProduct && (
        <div className="admin-page__card variant-options">
          <div className="variant-options__header">
            <h3 className="variant-options__title">{selectedProduct.name} - 옵션 관리</h3>
            <Button size="sm" variant="primary" onClick={() => openGroupModal()}>
              옵션 그룹 추가
            </Button>
          </div>

          {optionsLoading ? (
            <div className="admin-page__loading">불러오는 중...</div>
          ) : optionGroups.length === 0 ? (
            <div className="admin-page__loading">등록된 옵션 그룹이 없습니다.</div>
          ) : (
            optionGroups.map((group) => (
              <div key={group.id} className="option-group">
                <div className="option-group__header">
                  <div className="option-group__info">
                    <strong>{group.name}</strong>
                    {group.is_required && <Badge variant="rejected">필수</Badge>}
                    {group.allow_multiple && <Badge variant="info">복수 선택</Badge>}
                    {!group.is_active && <Badge variant="draft">비활성</Badge>}
                  </div>
                  <div className="option-group__actions">
                    <Button size="sm" variant="ghost" onClick={() => openValueModal(group.id)}>
                      값 추가
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openGroupModal(group)}>
                      수정
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => handleDeleteGroup(group.id)}>
                      삭제
                    </Button>
                  </div>
                </div>
                <div className="option-values">
                  {group.values.length === 0 ? (
                    <span className="option-values__empty">옵션 값이 없습니다.</span>
                  ) : (
                    group.values.map((val) => (
                      <div key={val.id} className="option-value">
                        <span className="option-value__name">{val.name}</span>
                        <span className="option-value__price">{val.price_delta > 0 ? `+${val.price_delta.toLocaleString()}원` : val.price_delta < 0 ? `${val.price_delta.toLocaleString()}원` : "0원"}</span>
                        {!val.is_active && <Badge variant="draft">비활성</Badge>}
                        <Button size="sm" variant="danger" onClick={() => handleDeleteValue(group.id, val.id)}>
                          삭제
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* 그룹 생성/수정 모달 */}
      <Modal
        open={groupModal.open}
        title={groupModal.editing ? "옵션 그룹 수정" : "옵션 그룹 추가"}
        width="440px"
        onClose={() => setGroupModal({ open: false, editing: null })}
        footer={
          <div className="modal-footer-actions">
            <Button variant="secondary" onClick={() => setGroupModal({ open: false, editing: null })}>
              취소
            </Button>
            <Button variant="primary" onClick={handleSaveGroup} disabled={saving || !groupForm.name}>
              {saving ? "저장 중..." : "저장"}
            </Button>
          </div>
        }
      >
        <div className="form-fields">
          <Input label="그룹명" value={groupForm.name} onChange={(e) => setGroupForm((p) => ({ ...p, name: e.target.value }))} placeholder="예: 색상, 사이즈" />
          <Input label="순서" type="number" value={String(groupForm.order)} onChange={(e) => setGroupForm((p) => ({ ...p, order: Number(e.target.value) }))} />
          <label className="checkbox-label">
            <input type="checkbox" checked={groupForm.is_required} onChange={(e) => setGroupForm((p) => ({ ...p, is_required: e.target.checked }))} />
            필수 선택
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={groupForm.allow_multiple} onChange={(e) => setGroupForm((p) => ({ ...p, allow_multiple: e.target.checked }))} />
            복수 선택 허용
          </label>
        </div>
      </Modal>

      {/* 값 추가 모달 */}
      <Modal
        open={valueModal.open}
        title="옵션 값 추가"
        width="440px"
        onClose={() => setValueModal({ open: false, groupId: null })}
        footer={
          <div className="modal-footer-actions">
            <Button variant="secondary" onClick={() => setValueModal({ open: false, groupId: null })}>
              취소
            </Button>
            <Button variant="primary" onClick={handleSaveValue} disabled={saving || !valueForm.name}>
              {saving ? "저장 중..." : "추가"}
            </Button>
          </div>
        }
      >
        <div className="form-fields">
          <Input label="값 이름" value={valueForm.name} onChange={(e) => setValueForm((p) => ({ ...p, name: e.target.value }))} placeholder="예: 빨강, M" />
          <Input label="가격 차이 (원)" type="number" value={valueForm.price_delta} onChange={(e) => setValueForm((p) => ({ ...p, price_delta: e.target.value }))} placeholder="0" />
          <Input label="순서" type="number" value={String(valueForm.order)} onChange={(e) => setValueForm((p) => ({ ...p, order: Number(e.target.value) }))} />
        </div>
      </Modal>
    </div>
  );
};

export default ProductVariants;
